import styles from './EntityPickerActions.module.scss';

import React, { type JSX, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ArticlePicker,
  IArticlePickerProps,
} from '@work4all/components/lib/components/entity-picker/article-picker/ArticlePicker';
import { EntityPickerPopover } from '@work4all/components/lib/components/entity-picker/components';
import { useEntityTemplate } from '@work4all/components/lib/components/entity-template-provider/EntityTemplateProvider';
import { CustomToolbarMappedAction } from '@work4all/components/lib/dataDisplay/actions-bar/ToolbarTypes';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { AddServiceContractPosTypeOfPos } from '@work4all/models/lib/Enums/AddServiceContractPosTypeOfPos.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { PickerTargetButton } from '../../containers/mask-overlays/locked-inputs';
import { usePositionTableActions } from '../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/hooks/use-position-table-actions';

interface ArticlePickerFieldProps
  extends Omit<IArticlePickerProps<false>, 'multiple' | 'size'> {
  error?: string;
  name?: string;
  label?: string;
  onChange: (
    value: Article | ErpPositionsKind | AddServiceContractPosTypeOfPos
  ) => void;
  showFunctions?: boolean;
  functions?: CustomToolbarMappedAction[];
  autoFocus?: boolean;
  input?: JSX.Element;
  required?: boolean;
  footerEntity?: Entities;
}

export const ArticlePickerField = React.forwardRef<
  HTMLDivElement,
  ArticlePickerFieldProps
>(function ArticlePickerField(props, ref) {
  const {
    error,
    placeholder,
    label,
    input,
    autoFocus,
    showFunctions = false,
    functions: functionsProp,
    favorites = true,
    required = false,
    footerEntity = Entities.article,
    ...pickerProps
  } = props;
  const { value, onChange } = pickerProps;

  const { t } = useTranslation();

  const popoverRef = useRef<EntityPickerPopover>(null);
  const placeholderText = placeholder ?? t('COMMON.ARTICLE');
  const lastAutoFocus = useRef(false);

  useEffect(() => {
    if (!popoverRef.current) return;
    if (autoFocus) {
      popoverRef.current.open();
    } else if (lastAutoFocus.current) {
      // detect failing edge
      popoverRef.current.close();
    }
    lastAutoFocus.current = autoFocus;
  }, [autoFocus]);

  const entityTemplate = useEntityTemplate();

  const defaultFunctions = usePositionTableActions({
    disabled: false,
    onAddSpecial: () => {},
  });

  const functions = functionsProp ?? defaultFunctions;
  const functionsWithoutFirst = useMemo(
    () => functions.slice(1, functions.length),
    [functions]
  );

  return (
    <EntityPickerPopover
      ref={popoverRef}
      persistSize
      onClose={pickerProps.onClose}
      width={'content'}
      picker={
        <ArticlePicker
          favorites={favorites}
          {...pickerProps}
          multiple={false}
          prefilter={pickerProps.prefilter}
          functions={showFunctions ? functionsWithoutFirst : undefined}
        />
      }
      footer={entityTemplate.renderPickerFooter({
        popoverRef,
        entity: footerEntity,
        prefilter: pickerProps.prefilter,
        ...props,
      })}
    >
      {input ?? (
        <PickerTargetButton
          ref={ref}
          name={props.name}
          value={value ? value?.name : ''}
          onClick={() => {
            popoverRef.current.open();
          }}
          required={required}
          label={
            label || (
              <>
                {!value && placeholderText}
                {value && (
                  <>
                    <span className={styles.primaryInfo}>
                      {placeholderText} &nbsp;
                    </span>
                    <span className={styles.secondaryInfo}>
                      {value?.number}
                    </span>
                  </>
                )}
              </>
            )
          }
          error={error}
          onClear={() => {
            onChange(null);
          }}
        />
      )}
    </EntityPickerPopover>
  );
});
